/**
 * AnnotationPresentation
 *
 * Renders downloadable presentation results inside assistant messages.
 *
 * Primary responsibilities:
 * - Resolve presentation tool results from annotation payloads
 * - Download generated presentation files
 *
 * Dependencies:
 * - Presentation platform tool result type
 * - Shared Button and Card primitives
 */

'use client'

import { useState } from 'react'
import { IconDownload, IconFile, IconPresentation } from '@tabler/icons-react'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { trackMeaningfulOutcomeCompleted } from '@/lib/analytics/meaningful-outcome'
import type { Annotation, Annotation2 } from '@/types/annotation'
import type { PresentationResult } from '@/types/platformTools'

const PRESENTATION_RESULT_KEYS = [
  'slideCreator__createPresentation',
  'slideCreator__addTitleSlide',
  'slideCreator__addContentSlide',
  'slideCreator__addChartSlide',
  'slideCreator__addTableSlide',
  'slideCreator__addImageSlide',
  'slideCreator__exportPresentation'
] as const

type PresentationAnnotationRecord = Record<
  (typeof PRESENTATION_RESULT_KEYS)[number],
  { result?: PresentationResult } | undefined
>

const getPresentationResult = (annotation: Annotation | Annotation2) => {
  const annotationRecord = annotation as unknown as Partial<PresentationAnnotationRecord>

  for (const key of PRESENTATION_RESULT_KEYS) {
    const result = annotationRecord[key]?.result

    if (result?.url) {
      return result
    }
  }

  return null
}

export default function AnnotationPresentation({
  annotation
}: {
  annotation: Annotation | Annotation2
}) {
  const [isDownloading, setIsDownloading] = useState(false)
  const result = getPresentationResult(annotation)

  if (!result || !result.url) {
    return null
  }

  const handleDownload = async () => {
    if (!result?.url) {
      return
    }

    setIsDownloading(true)

    try {
      const response = await fetch(
        `/api/presentation/download?url=${encodeURIComponent(result.url)}`
      )
      if (!response.ok) throw new Error('Network response was not ok')

      const blob = await response.blob()
      const blobUrl = window.URL.createObjectURL(blob)

      const link = document.createElement('a')
      link.href = blobUrl
      link.download = result.fileName || 'presentation.pptx'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      window.URL.revokeObjectURL(blobUrl)
      void trackMeaningfulOutcomeCompleted({
        surface: 'chat',
        outcome_type: 'artifact',
        qualifying_action: 'exported',
        artifact_id: result.presentationId,
        source: 'presentation'
      })
    } catch (error) {
      console.error('Download failed:', error)
    } finally {
      setIsDownloading(false)
    }
  }

  return (
    <Card className="flex max-w-md items-center gap-3 border-border/70 bg-card p-3">
      <div className="flex size-10 shrink-0 items-center justify-center rounded-md border border-border bg-muted text-muted-foreground">
        <IconPresentation className="size-5" />
      </div>

      <div className="min-w-0 flex-1">
        <div className="mb-1 flex items-center gap-2">
          <IconFile className="size-4 text-muted-foreground" />
          <span className="truncate text-sm font-medium text-foreground">
            {result.fileName || 'presentation.pptx'}
          </span>
        </div>

        <div className="space-y-0.5 text-xs text-muted-foreground">
          {result.title && <div>Title: {result.title}</div>}
          {result.slideCount && (
            <div>
              {result.slideCount} slide{result.slideCount !== 1 ? 's' : ''}
            </div>
          )}
          {result.fileSize && <div>{(result.fileSize / 1024 / 1024).toFixed(2)} MB</div>}
          {result.description && <div className="max-w-xs truncate">{result.description}</div>}
        </div>
      </div>

      <div className="shrink-0">
        <Button
          onClick={handleDownload}
          disabled={isDownloading}
          size="sm"
          variant="outline"
          className="h-8 px-2.5"
          loading={isDownloading}
        >
          <IconDownload className="size-4" />
          <span className="text-xs">Download</span>
        </Button>
      </div>
    </Card>
  )
}
